import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Plus, Trash2, Check, Copy, RefreshCw, ExternalLink, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import {
  getCustomDomains, addCustomDomain, deleteCustomDomain,
  verifyCustomDomain, provisionCloudflare, getCustomDomainStatus,
} from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

interface CustomDomainSectionProps {
  projectId: string;
  landingPages: Array<{ landing_page_id: string; title: string; slug: string; status: string }>;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px',
  border: '1px solid var(--stroke)', borderRadius: '6px',
  fontSize: '0.85rem', background: 'var(--panel)',
  color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle };

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '4px',
  fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500,
};

const STATUS_BADGE: Record<string, { variant: string; label: string }> = {
  pending: { variant: 'warning', label: 'Pending DNS' },
  dns_verified: { variant: 'info', label: 'DNS Verified' },
  ssl_provisioning: { variant: 'info', label: 'SSL Provisioning' },
  active: { variant: 'success', label: 'Active' },
  error: { variant: 'danger', label: 'Error' },
};

const CustomDomainSection: React.FC<CustomDomainSectionProps> = ({ projectId, landingPages }) => {
  const [domains, setDomains] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add form state
  const [newDomain, setNewDomain] = useState('');
  const [newMethod, setNewMethod] = useState('letsencrypt');
  const [newLpId, setNewLpId] = useState('');

  // DNS instructions (shown after adding or clicking Settings)
  const [dnsInstructions, setDnsInstructions] = useState<any | null>(null);
  const [copied, setCopied] = useState('');
  const [expandedDomainId, setExpandedDomainId] = useState<string | null>(null);

  const publishedLPs = landingPages.filter(lp => lp.status === 'published');

  // Build DNS instructions from existing domain record
  const buildInstructions = (d: any) => {
    const parts = (d.domain || '').split('.');
    const isApex = parts.length <= 2;
    const host = isApex ? d.domain : parts[0];
    const rootDomain = isApex ? d.domain : parts.slice(1).join('.');
    const txtHost = isApex ? '_aime-verify' : `_aime-verify.${host}`;
    return {
      cname: { host, name: d.domain, value: 'pages.aime.clearbyte.au', type: 'CNAME', root_domain: rootDomain },
      txt: { host: txtHost, name: `_aime-verify.${d.domain}`, value: d.verification_txt, type: 'TXT', root_domain: rootDomain },
      is_apex: isApex,
    };
  };

  const loadDomains = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await getCustomDomains(projectId);
      setDomains(data || []);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  const handleAdd = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    setError('');
    try {
      const result = await addCustomDomain({
        domain: newDomain.trim().toLowerCase(),
        project_id: projectId,
        method: newMethod,
        landing_page_id: newLpId || undefined,
      });
      setDnsInstructions(result.dns_instructions);
      setNewDomain('');
      setNewLpId('');
      setSuccess('Domain added — follow the DNS instructions below.');
      setTimeout(() => setSuccess(''), 5000);
      await loadDomains();

      // If Cloudflare method, auto-provision
      if (newMethod === 'cloudflare' && result.domain_id) {
        try {
          await provisionCloudflare(result.domain_id);
        } catch {
          // non-critical — user can provision manually later
        }
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to add domain'));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (domainId: string, domainName: string) => {
    if (!window.confirm(`Remove custom domain ${domainName}?`)) return;
    try {
      await deleteCustomDomain(domainId);
      setDomains(prev => prev.filter(d => d.domain_id !== domainId));
    } catch (err: any) {
      setError(getApiError(err, 'Failed to remove domain'));
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifying(domainId);
    setError('');
    try {
      const result = await verifyCustomDomain(domainId);
      if (result.verified) {
        setSuccess('DNS verified successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(`Verification incomplete: ${(result.errors || []).join(', ') || 'DNS records not found yet. Allow up to 48 hours for propagation.'}`);
      }
      await loadDomains();
    } catch (err: any) {
      setError(getApiError(err, 'Verification failed'));
    } finally {
      setVerifying(null);
    }
  };

  const handlePollStatus = async (domainId: string) => {
    try {
      await getCustomDomainStatus(domainId);
      await loadDomains();
    } catch {
      // ignore
    }
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <GlassCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Globe size={18} style={{ color: 'var(--primary-500, #2177FF)' }} />
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>Custom Domain</h3>
      </div>
      <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
        Serve your landing pages on your own domain (e.g. <code>offer.yourbrand.com</code>).
      </p>

      {/* Existing domains */}
      {loading ? (
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Loading...</p>
      ) : domains.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {domains.map(d => {
            const badge = STATUS_BADGE[d.status] || { variant: 'default', label: d.status };
            const isExpanded = expandedDomainId === d.domain_id;
            const instructions = buildInstructions(d);
            return (
              <div key={d.domain_id} style={{
                background: 'var(--panel-strong)', borderRadius: '8px', border: '1px solid var(--stroke)',
                fontSize: '0.85rem', overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', flexWrap: 'wrap',
                }}>
                  <ExternalLink size={14} style={{ color: 'var(--text-secondary)' }} />
                  <strong style={{ flex: 1, minWidth: '120px' }}>{d.domain}</strong>
                  <GlassBadge variant={badge.variant as any}>{badge.label}</GlassBadge>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                    {d.method === 'cloudflare' ? 'Cloudflare' : "Let's Encrypt"}
                  </span>
                  {d.status === 'pending' && (
                    <GlassButton variant="secondary" onClick={() => handleVerify(d.domain_id)}
                      disabled={verifying === d.domain_id}
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                      <RefreshCw size={12} />
                      {verifying === d.domain_id ? 'Checking...' : 'Verify'}
                    </GlassButton>
                  )}
                  {d.status === 'ssl_provisioning' && (
                    <GlassButton variant="secondary" onClick={() => handlePollStatus(d.domain_id)}
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                      <RefreshCw size={12} /> Check SSL
                    </GlassButton>
                  )}
                  <GlassButton variant="secondary"
                    onClick={() => setExpandedDomainId(isExpanded ? null : d.domain_id)}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                    <Settings size={12} />
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </GlassButton>
                  <GlassButton variant="danger" onClick={() => handleDelete(d.domain_id, d.domain)}
                    style={{ padding: '4px 8px' }}>
                    <Trash2 size={13} />
                  </GlassButton>
                </div>

                {/* Expanded settings panel */}
                {isExpanded && (
                  <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--stroke)' }}>
                    {/* DNS Instructions */}
                    <div style={{ marginTop: '12px', fontSize: '0.82rem' }}>
                      <strong style={{ fontSize: '0.8rem' }}>DNS Records ({instructions.cname.root_domain})</strong>

                      {instructions.is_apex && (
                        <div style={{
                          padding: '8px 10px', margin: '8px 0',
                          background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)',
                          borderRadius: '6px', fontSize: '0.78rem',
                        }}>
                          <strong>Important:</strong> Use a subdomain like <code>pages.{instructions.cname.root_domain}</code> — enter <strong>pages</strong> as the CNAME name at your DNS provider.
                        </div>
                      )}

                      <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--stroke)', textAlign: 'left' }}>
                            <th style={{ padding: '4px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                            <th style={{ padding: '4px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>Host</th>
                            <th style={{ padding: '4px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>Value</th>
                            <th style={{ padding: '4px 6px', width: '28px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ background: 'var(--panel)', fontFamily: 'monospace' }}>
                            <td style={{ padding: '6px' }}>CNAME</td>
                            <td style={{ padding: '6px' }}>{instructions.cname.host}</td>
                            <td style={{ padding: '6px' }}>{instructions.cname.value}</td>
                            <td style={{ padding: '6px' }}>
                              <button onClick={() => copyToClipboard(instructions.cname.value, `cname-${d.domain_id}`)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }}>
                                {copied === `cname-${d.domain_id}` ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                            </td>
                          </tr>
                          <tr style={{ background: 'var(--panel)', fontFamily: 'monospace' }}>
                            <td style={{ padding: '6px' }}>TXT</td>
                            <td style={{ padding: '6px' }}>{instructions.txt.host}</td>
                            <td style={{ padding: '6px', wordBreak: 'break-all' }}>{instructions.txt.value}</td>
                            <td style={{ padding: '6px' }}>
                              <button onClick={() => copyToClipboard(instructions.txt.value, `txt-${d.domain_id}`)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }}>
                                {copied === `txt-${d.domain_id}` ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add domain form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Domain</label>
          <input style={inputStyle} placeholder="pages.yourbrand.com" value={newDomain}
            onChange={e => setNewDomain(e.target.value)} />
          <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
            Enter a subdomain such as <code>pages.yourbrand.com</code> or <code>lp.yourbrand.com</code>.
            The part before your domain (e.g. <strong>pages</strong>) is what you'll enter as the CNAME name at your DNS provider.
          </p>
        </div>
        <div>
          <label style={labelStyle}>SSL Method</label>
          <select style={selectStyle} value={newMethod} onChange={e => setNewMethod(e.target.value)}>
            <option value="letsencrypt">Let's Encrypt (Self-Managed DNS)</option>
            <option value="cloudflare">Cloudflare (Automatic)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Primary Landing Page</label>
          <select style={selectStyle} value={newLpId} onChange={e => setNewLpId(e.target.value)}>
            <option value="">— Auto (latest published) —</option>
            {publishedLPs.map(lp => (
              <option key={lp.landing_page_id} value={lp.landing_page_id}>{lp.title}</option>
            ))}
          </select>
        </div>
      </div>
      <GlassButton variant="primary" onClick={handleAdd} disabled={adding || !newDomain.trim()}
        style={{ alignSelf: 'flex-start' }}>
        <Plus size={15} /> {adding ? 'Adding...' : 'Add Domain'}
      </GlassButton>

      {/* DNS Instructions */}
      {dnsInstructions && (
        <div style={{
          background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
          borderRadius: '8px', padding: '16px', fontSize: '0.82rem',
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', fontWeight: 600 }}>DNS Setup Instructions</h4>

          {dnsInstructions.is_apex && (
            <div style={{
              padding: '10px 12px', marginBottom: '12px',
              background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)',
              borderRadius: '6px', fontSize: '0.8rem', color: 'var(--text)',
            }}>
              <strong>Important:</strong> You need to use a subdomain.
              For example, enter <code>pages.{dnsInstructions.cname.root_domain}</code> or <code>lp.{dnsInstructions.cname.root_domain}</code> instead
              of <code>{dnsInstructions.cname.root_domain}</code> on its own.
              At your DNS provider, you would enter <strong>pages</strong> (or <strong>lp</strong>) as the CNAME name.
            </div>
          )}

          <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)' }}>
            Add these records at your DNS provider ({dnsInstructions.cname.root_domain}):
          </p>

          <div style={{ marginBottom: '12px' }}>
            <strong>1. CNAME Record</strong>
            <table style={{ width: '100%', marginTop: '6px', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--stroke)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Host / Name</th>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Points to / Value</th>
                  <th style={{ padding: '6px 8px', width: '32px' }}></th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'var(--panel)', fontFamily: 'monospace' }}>
                  <td style={{ padding: '8px' }}>CNAME</td>
                  <td style={{ padding: '8px' }}>{dnsInstructions.cname.host}</td>
                  <td style={{ padding: '8px' }}>{dnsInstructions.cname.value}</td>
                  <td style={{ padding: '8px' }}>
                    <button onClick={() => copyToClipboard(dnsInstructions.cname.value, 'cname')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }}>
                      {copied === 'cname' ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <strong>2. TXT Record (verification)</strong>
            <table style={{ width: '100%', marginTop: '6px', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--stroke)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Host / Name</th>
                  <th style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Value</th>
                  <th style={{ padding: '6px 8px', width: '32px' }}></th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: 'var(--panel)', fontFamily: 'monospace' }}>
                  <td style={{ padding: '8px' }}>TXT</td>
                  <td style={{ padding: '8px' }}>{dnsInstructions.txt.host}</td>
                  <td style={{ padding: '8px', wordBreak: 'break-all' }}>{dnsInstructions.txt.value}</td>
                  <td style={{ padding: '8px' }}>
                    <button onClick={() => copyToClipboard(dnsInstructions.txt.value, 'txt')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }}>
                      {copied === 'txt' ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ margin: '0', fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            DNS changes can take up to 48 hours to propagate. Click "Verify" on the domain once both records are in place.
          </p>
        </div>
      )}

      {/* Status messages */}
      {error && <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--danger, #dc2626)' }}>{error}</p>}
      {success && <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--success, #16a34a)' }}>{success}</p>}
    </GlassCard>
  );
};

export default CustomDomainSection;
